import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  TablePagination,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './NCStatementsManagement.css';
import { LoaderWrapper } from '../../components/index';
import { 
  getNCStatementsList, 
  addNCStatement, 
  updateNCStatement, 
  deleteNCStatement,
  getStandardsByField,
  getCategoriesByFieldAndStandard,
  getDisplayIdsByCategory 
} from '../../services/api';

const NCStatementsManagement = () => {
  // Utility functions for field type management (defined first)
  const getAllowedFieldTypes = () => {
    const labTypeIds = localStorage.getItem('labTypeIds');
    const labTypeId = localStorage.getItem('labTypeId');
    
    if (!labTypeIds && !labTypeId) {
      return [];
    }
    
    const allowedTypes = [];
    const typeIds = labTypeIds ? labTypeIds.split(',') : [labTypeId];
    
    typeIds.forEach(id => {
      switch(id.trim()) {
        case '1':
          allowedTypes.push({ value: 'Testing', label: 'Testing' });
          break;
        case '3':
          allowedTypes.push({ value: 'Medical', label: 'Medical' });
          break;
        case '2':
          allowedTypes.push({ value: 'Calibration', label: 'Calibration' });
          break;
        case '4':
          allowedTypes.push({ value: 'PTP', label: 'PTP' });
          break;
        case '5':
          allowedTypes.push({ value: 'RMP', label: 'RMP' });
          break;
      }
    });
    
    return allowedTypes;
  };

  const shouldDisableFieldDropdown = () => {
    return getAllowedFieldTypes().length <= 1;
  };

  const getDefaultFieldType = () => {
    const allowedTypes = getAllowedFieldTypes();
    return allowedTypes.length === 1 ? allowedTypes[0].value : '0';
  };

  const fieldTypes = [
    { value: '0', label: 'Select Field' },
    ...getAllowedFieldTypes()
  ];

  const filteredFieldTypes = getAllowedFieldTypes();

  const [loading, setLoading] = useState(false);
  const [ncStatementsList, setNCStatementsList] = useState([]);
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [displayIds, setDisplayIds] = useState([]);
  
  // Quill editor configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet'
  ];
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filter state
  const [filters, setFilters] = useState({
    field: '',
    standard: '',
    category: '',
    displayId: '',
    name: '',
  });
  
  // Form state
  const [formData, setFormData] = useState({
    field: '0',
    standard: '',
    categoryId: '',
    displayId: '',
    name: '',
  });
  
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchNCStatementsList();
    // Initialize form with default field type from localStorage
    const defaultField = getDefaultFieldType();
    setFormData(prev => ({ ...prev, field: defaultField }));
    setFilters(prev => ({ ...prev, field: defaultField }));
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Fetch standards when field changes
  useEffect(() => {
    if (formData.field && formData.field !== '0') {
      fetchStandards(formData.field);
    } else {
      setStandards([]);
      setFormData(prev => ({ ...prev, standard: '', categoryId: '', displayId: '' }));
      setCategories([]);
      setDisplayIds([]);
    }
  }, [formData.field]);

  // Fetch categories when standard changes
  useEffect(() => {
    if (formData.standard && formData.field && formData.field !== '0') {
      fetchCategories(formData.field, formData.standard);
    } else {
      setCategories([]);
      setFormData(prev => ({ ...prev, categoryId: '', displayId: '' }));
      setDisplayIds([]);
    }
  }, [formData.standard]);

  // Fetch display IDs when category changes
  useEffect(() => {
    if (formData.categoryId) {
      fetchDisplayIds(formData.categoryId);
    } else {
      setDisplayIds([]);
      setFormData(prev => ({ ...prev, displayId: '' }));
    }
  }, [formData.categoryId]);

  const fetchNCStatementsList = async () => {
    try {
      setLoading(true);
      const response = await getNCStatementsList();
      if (response.data.success) {
        setNCStatementsList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching NC statements data:', error);
      showSnackbar('Error fetching NC statements data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStandards = async (field) => {
    try {
      const response = await getStandardsByField(field);
      if (response.data.success) {
        setStandards(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching standards:', error);
      showSnackbar('Error fetching standards', 'error');
    }
  };

  const fetchCategories = async (field, standard) => {
    try {
      const response = await getCategoriesByFieldAndStandard(field, standard);
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar('Error fetching categories', 'error');
    }
  };

  const fetchDisplayIds = async (categoryId) => {
    try {
      const response = await getDisplayIdsByCategory(categoryId);
      if (response.data.success) {
        setDisplayIds(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching display IDs:', error);
      showSnackbar('Error fetching display IDs', 'error');
    }
  };

  // Memoized filtered list
  const filteredNCStatementsList = useMemo(() => {
    let filtered = ncStatementsList;

    if (filters.field) {
      filtered = filtered.filter(item => item.Field === filters.field);
    }

    if (filters.standard) {
      filtered = filtered.filter(item => item.Standard === filters.standard);
    }

    if (filters.category) {
      filtered = filtered.filter(item => item.CatName === filters.category);
    }

    if (filters.displayId) {
      filtered = filtered.filter(item => item.clauseno === filters.displayId);
    }

    if (filters.name) {
      filtered = filtered.filter(item => 
        item.Name && item.Name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    return filtered;
  }, [ncStatementsList, filters]);

  // Paginated list
  const paginatedNCStatementsList = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredNCStatementsList.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredNCStatementsList, page, rowsPerPage]);

  const clearFilters = useCallback(() => {
    setFilters({
      field: getDefaultFieldType(),
      standard: '',
      category: '',
      displayId: '',
      name: '',
    });
    setPage(0);
  }, []);

  // Get unique values for filter dropdowns - memoized
  const uniqueFields = useMemo(() => {
    const allowedTypes = getAllowedFieldTypes().map(type => type.value);
    return [...new Set(ncStatementsList.map(item => item.Field).filter(field => 
      field && allowedTypes.includes(field)
    ))];
  }, [ncStatementsList]);

  const uniqueStandards = useMemo(() => {
    let standards = [...new Set(ncStatementsList.map(item => item.Standard).filter(Boolean))];
    
    if (filters.field) {
      standards = [...new Set(
        ncStatementsList
          .filter(item => item.Field === filters.field)
          .map(item => item.Standard)
          .filter(Boolean)
      )];
    }
    
    return standards;
  }, [ncStatementsList, filters.field]);

  const uniqueCategories = useMemo(() => {
    let categories = [...new Set(ncStatementsList.map(item => item.CatName).filter(Boolean))];
    
    if (filters.standard) {
      categories = [...new Set(
        ncStatementsList
          .filter(item => item.Standard === filters.standard)
          .map(item => item.CatName)
          .filter(Boolean)
      )];
    }
    
    return categories;
  }, [ncStatementsList, filters.standard]);

  const uniqueDisplayIds = useMemo(() => {
    return [...new Set(ncStatementsList.map(item => item.clauseno).filter(Boolean))];
  }, [ncStatementsList]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.field || formData.field === '0') {
      newErrors.field = 'Field Type is required';
    }
    
    if (!formData.standard) {
      newErrors.standard = 'Standard is required';
    }
    
    if (!formData.displayId || formData.displayId === '') {
      newErrors.displayId = 'Display Id is required';
    }
    
    // Check if name has actual content (not just empty HTML tags)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formData.name;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (!formData.name || textContent.trim() === '') {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await addNCStatement({
        field: formData.field,
        standard: formData.standard,
        clauseid: formData.categoryId || null,
        clauseno: formData.displayId,
        name: formData.name,
      });
      
      if (response.data.success) {
        showSnackbar('NC Statement added successfully', 'success');
        resetForm();
        fetchNCStatementsList();
        setPage(0);
      } else {
        showSnackbar(response.data.message || 'Error adding NC statement', 'error');
      }
    } catch (error) {
      console.error('Error adding NC statement:', error);
      const errorMessage = error.response?.data?.message || 'Error adding NC statement';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [formData]);

  const handleUpdate = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await updateNCStatement({
        id: editId,
        field: formData.field,
        standard: formData.standard,
        clauseid: formData.categoryId || null,
        clauseno: formData.displayId,
        name: formData.name,
      });
      
      if (response.data.success) {
        showSnackbar('NC Statement updated successfully', 'success');
        resetForm();
        fetchNCStatementsList();
      } else {
        showSnackbar(response.data.message || 'Error updating NC statement', 'error');
      }
    } catch (error) {
      console.error('Error updating NC statement:', error);
      const errorMessage = error.response?.data?.message || 'Error updating NC statement';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, editId]);

  const handleEdit = useCallback(async (item) => {
    console.log('Editing NC statement:', item);
    
    // Convert plain text to HTML if it's not already HTML
    let nameContent = item.Name || '';
    if (nameContent && !nameContent.includes('<')) {
      // Convert plain text to HTML paragraphs
      nameContent = nameContent.split('\n').map(line => 
        line.trim() ? `<p>${line}</p>` : '<p><br></p>'
      ).join('');
    }
    
    setFormData({
      field: item.Field,
      standard: item.Standard,
      categoryId: item.clauseid || '',
      displayId: item.clauseno || '',
      name: nameContent,
    });
    setEditMode(true);
    setEditId(item.id);
    
    // Fetch cascading dropdowns for edit mode
    if (item.Field && item.Field !== '0') {
      await fetchStandards(item.Field);
      if (item.Standard) {
        await fetchCategories(item.Field, item.Standard);
        if (item.clauseid) {
          await fetchDisplayIds(item.clauseid);
        }
      }
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleDeleteClick = useCallback((id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deleteNCStatement(deleteId);
      
      if (response.data.success) {
        showSnackbar('NC Statement deleted successfully', 'success');
        fetchNCStatementsList();
      } else {
        showSnackbar(response.data.message || 'Error deleting NC statement', 'error');
      }
    } catch (error) {
      console.error('Error deleting NC statement:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting NC statement';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  }, [deleteId]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      field: getDefaultFieldType(),
      standard: '',
      categoryId: '',
      displayId: '',
      name: '',
    });
    setEditMode(false);
    setEditId(null);
    setErrors({});
    setStandards([]);
    setCategories([]);
    setDisplayIds([]);
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Pagination handlers
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        {editMode ? 'Edit NC Statement' : 'Add NC Statement'}
      </Typography>

      {/* Form Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={editMode ? handleUpdate : handleSubmit}>
          <Grid container spacing={3}>
            {/* Field Type */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.field}
                required
              >
                <InputLabel>Field Type</InputLabel>
                <Select
                  name="field"
                  value={formData.field}
                  onChange={handleInputChange}
                  label="Field Type"
                  disabled={shouldDisableFieldDropdown()}
                >
                  {fieldTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.field && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.field}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Standard */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.standard}
                required
                disabled={!formData.field || formData.field === '0'}
              >
                <InputLabel>Standard</InputLabel>
                <Select
                  name="standard"
                  value={formData.standard}
                  onChange={handleInputChange}
                  label="Standard"
                >
                  <MenuItem value="">
                    <em>Select Standard</em>
                  </MenuItem>
                  {standards.map((std) => (
                    <MenuItem key={std.Id || std.id} value={std.standard || std.Standard}>
                      {std.standard || std.Standard}
                    </MenuItem>
                  ))}
                </Select>
                {errors.standard && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.standard}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Category Name */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                disabled={!formData.standard}
              >
                <InputLabel>Category Name</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  label="Category Name"
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.Id || cat.id} value={cat.Id || cat.id}>
                      {cat.CatName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Display Id */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.displayId}
                required
                disabled={!formData.categoryId}
              >
                <InputLabel>Display Id</InputLabel>
                <Select
                  name="displayId"
                  value={formData.displayId}
                  onChange={handleInputChange}
                  label="Display Id"
                >
                  <MenuItem value="">
                    <em>Select Display Id</em>
                  </MenuItem>
                  {displayIds.map((displayId) => (
                    <MenuItem key={displayId.Id || displayId.id} value={displayId.DisplayName}>
                      {displayId.DisplayName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.displayId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.displayId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Name */}
            <Grid item xs={12}>
              <Box>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 1,
                    color: errors.name ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
                  }}
                >
                  Name <span style={{ color: '#d32f2f' }}>*</span>
                </Typography>
                <Box
                  sx={{
                    border: errors.name ? '1px solid #d32f2f' : '1px solid #c4c4c4',
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: errors.name ? '#d32f2f' : '#000',
                    },
                    '& .quill': {
                      backgroundColor: '#fff',
                    },
                    '& .ql-container': {
                      minHeight: '120px',
                      fontSize: '16px',
                    }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.name}
                    onChange={(content) => {
                      setFormData(prev => ({ ...prev, name: content }));
                      if (errors.name) {
                        setErrors(prev => ({ ...prev, name: '' }));
                      }
                    }}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter name with formatting..."
                  />
                </Box>
                {errors.name && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                    {errors.name}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color={editMode ? 'error' : 'primary'}
                  disabled={loading}
                >
                  {editMode ? 'Update' : 'Submit'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#e0ebeb',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            NC Statements Management
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchNCStatementsList}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {/* Filter Section */}
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FilterListIcon sx={{ mr: 1, color: '#666' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                  Filter NC Statements
                </Typography>
              </Box>
            </Grid>
            
            {/* Field Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueFields}
                value={filters.field || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, field: newValue || '', standard: '', category: '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Field" placeholder="Search" />
                )}
                clearOnEscape
                disabled={shouldDisableFieldDropdown()}
              />
            </Grid>

            {/* Standard Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueStandards}
                value={filters.standard || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, standard: newValue || '', category: '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Standard" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueCategories}
                value={filters.category || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, category: newValue || '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Category" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Display Id Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueDisplayIds}
                value={filters.displayId || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, displayId: newValue || '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Display Id" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Name Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                size="small"
                label="Search Name"
                placeholder="Type to search..."
                value={filters.name || ''}
                onChange={(event) => {
                  setFilters(prev => ({ ...prev, name: event.target.value || '' }));
                }}
                fullWidth
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
                color="secondary"
              >
                Clear All Filters
              </Button>
              <Typography variant="caption" sx={{ ml: 2, color: '#666' }}>
                Showing {filteredNCStatementsList.length} of {ncStatementsList.length} items
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>Actions</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>Field</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>Standard</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 200 }}>Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>Display Id</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 150 }}>Category Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNCStatementsList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {ncStatementsList.length === 0 ? 'No NC statements found' : 'No items match the selected filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedNCStatementsList.map((item, index) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(item)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(item.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.Field || 'N/A'} variant='outlined' size='small' color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.Standard || 'N/A'} variant='outlined' size='small' color="secondary" />
                    </TableCell>
                    <TableCell align="left">
                      <Box 
                        className="ncstatements-html-content"
                        dangerouslySetInnerHTML={{ __html: item.Name || 'N/A' }} 
                      />
                    </TableCell>
                    <TableCell align="center">{item.clauseno || 'N/A'}</TableCell>
                    <TableCell align="center">{item.CatName || 'N/A'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredNCStatementsList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Items per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this NC statement?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NCStatementsManagement;
